import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import './ThemeToggle.css';

const STORAGE_KEY = 'ds-theme';

function getInitialTheme() {
  const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored === 'dark' || stored === 'light') return stored;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M12 2.5v2.5M12 19v2.5M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2.5 12H5M19 12h2.5M4.2 19.8L6 18M18 6l1.8-1.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20.5 14.2a8.5 8.5 0 1 1-9.7-11 7 7 0 0 0 9.7 11Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

function ThemeToggle({ collapsed }) {
  const [theme, setTheme] = useState(getInitialTheme);
  const reduceMotion = useReducedMotion();

  // Keep the DOM attribute correct even on the very first mount (in case
  // getInitialTheme picked something other than the default) and whenever
  // theme changes via a path that didn't already set it (e.g. future
  // programmatic use).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const isDark = theme === 'dark';

  const toggle = (event) => {
    const next = isDark ? 'light' : 'dark';

    const applyNext = () => {
      document.documentElement.setAttribute('data-theme', next);
      setTheme(next);
      localStorage.setItem(STORAGE_KEY, next);
    };

    // Circular reveal wipe expanding from the click point, using the View
    // Transitions API. Falls back to a plain instant switch (still eased
    // by the background/color transition on .ds-root) on browsers without
    // support, or when the user asked for reduced motion.
    if (reduceMotion || !document.startViewTransition) {
      applyNext();
      return;
    }

    const x = event.clientX;
    const y = event.clientY;
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    );

    const transition = document.startViewTransition(() => flushSync(applyNext));

    transition.ready.then(() => {
      document.documentElement.animate(
        {
          clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`],
        },
        {
          duration: 550,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        }
      );
    });
  };

  return (
    <button
      className="ds-theme-toggle"
      onClick={toggle}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label="Toggle color theme"
    >
      <motion.span
        className="ds-theme-toggle-icon"
        initial={false}
        animate={{ rotate: isDark ? 0 : 180 }}
        transition={{ duration: reduceMotion ? 0 : 0.25, ease: [0.4, 0, 0.2, 1] }}
      >
        {isDark ? <MoonIcon /> : <SunIcon />}
      </motion.span>
      {!collapsed && <span className="ds-theme-toggle-label">{isDark ? 'Dark mode' : 'Light mode'}</span>}
    </button>
  );
}

export default ThemeToggle;
